import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import { Chip, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  ChatbotPlaceholderValueLanguage,
  ChatbotTemplatePlaceholder,
} from '../../model/types';

export interface PlaceholderChipsProps {
  placeholders: ChatbotTemplatePlaceholder[];
  onInsert: (placeholder: string) => void;
  disabled?: boolean;
}

const TOOLTIP_KEY: Partial<Record<ChatbotPlaceholderValueLanguage, string>> = {
  CODE: 'editor.placeholderLanguage.CODE',
  LOCALIZED: 'editor.placeholderLanguage.LOCALIZED',
};

/**
 * Clickable mono chips inserting `{{name}}` at the caret — the set is per **key**
 * (`item.allowedPlaceholders`, templates doc §13.6), not per kind. A `CODE` placeholder carries a
 * marker + tooltip explaining it is not translated.
 */
export function PlaceholderChips({
  placeholders,
  onInsert,
  disabled = false,
}: PlaceholderChipsProps) {
  const { t } = useTranslation('chatbot');
  if (placeholders.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
      {placeholders.map((placeholder) => {
        const tooltipKey = TOOLTIP_KEY[placeholder.valueLanguage];
        const chip = (
          <Chip
            key={placeholder.name}
            size="small"
            variant="outlined"
            clickable
            disabled={disabled}
            onClick={() => onInsert(placeholder.name)}
            icon={
              placeholder.valueLanguage === 'CODE' ? (
                <CodeRoundedIcon sx={{ fontSize: 14 }} />
              ) : undefined
            }
            label={`{{${placeholder.name}}}`}
            aria-label={t('editor.insertPlaceholder', { placeholder: `{{${placeholder.name}}}` })}
            aria-description={tooltipKey ? t(tooltipKey) : undefined}
            sx={{ fontFamily: 'monospace' }}
          />
        );
        return tooltipKey ? (
          <Tooltip key={placeholder.name} title={t(tooltipKey)}>
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      })}
    </Stack>
  );
}
